import { type PaletteKey, sprite } from '../../../lib';
import { box, byRow, dots, geometry, outlined, round, taper } from '../effect-guns.sprite';

/**
 * Permafrost (the Glacier set's gun, ADR-0160 amended): a crystal cryo cannon. Steel frosted along its top edges, a
 * cluster of ice crystals growing out of a cradle on its back where the Pollinator's seed jar is (each lit white along
 * its left facet, the big one with a cold cyan heart and a glint climbing it), ice crusted over its front, a snowflake
 * on its side, a barrel ringed with frost narrowing to an icicle at the muzzle.
 */

const STEEL = ['k', '1', '2', '3'] as const;
const FROSTED = ['3', '4', '5', 'w'] as const;

/** Crystals growing out of its back: [foot x, foot y, length, half-width, lean (radians forward of straight up)]. */
const CLUSTER = [
  [26, 14, 9, 2.6, -0.45],
  [42, 14, 10, 2.6, 0.82],
  [33.5, 14, 13, 4, 0.36],
] as const;

/** Where (x, y) is on a crystal of the cluster: across it (-1 … 1, its lit side first) and up it (0 … 1); or null. */
function onCrystal(x: number, y: number, [fx, fy, len, half, lean]: (typeof CLUSTER)[number]): [number, number] | null {
  const [ax, ay] = [Math.sin(lean), -Math.cos(lean)];
  const [dx, dy] = [x + 0.5 - fx, y + 0.5 - fy];
  const t = (dx * ax + dy * ay) / len;
  if (t < -0.2 || t > 1) return null;
  const w = half * (t < 0.6 ? 1 : (1 - t) / 0.4);
  const d = (dx * -ay + dy * ax) / Math.max(0.01, w);
  return Math.abs(d) < 1 ? [d, t] : null;
}

const gun = (frame: number) =>
  outlined((c) => {
    // The stock: steel, frosted along its top, a pale butt plate.
    box(c, 5, 16, 22, 25, [2, 1], byRow(16, 25, STEEL, 1, 2), 'l');
    c.hline(7, 21, 16, '5');
    box(c, 5, 17, 7, 24, [1], (_x, y) => (y < 19 ? '5' : y > 22 ? '3' : '4'), 'l');
    // The body: steel, frost along its top edge dripping down its side, ice crusted over its front.
    box(c, 22, 13, 46, 26, [2, 1], byRow(13, 26, STEEL, 1, 3), 'r');
    c.hline(23, 45, 14, 'w');
    dots(c, 'w', [
      [24, 15],
      [28, 15],
      [28, 16],
    ]);
    dots(c, '5', [
      [25, 15],
      [29, 15],
      [24, 16],
    ]);
    for (let y = 14; y <= 25; y++) {
      const reach = 41 + Math.round(Math.sin(y * 1.7) * 1.2 + (y - 14) * 0.15);
      for (let x = reach; x <= 46; x++) if (c.get(x, y) !== null) c.set(x, y, x === reach ? '4' : y > 22 ? '4' : '5');
    }
    // Crystals growing out of its back: each lit along its left facet, shaded on its right; a cold cyan heart in the
    // big one, a glint climbing it.
    for (let y = 2; y <= 14; y++)
      for (let x = 18; x <= 54; x++) {
        let hit: [number, number] | null = null;
        let big = false;
        for (const [i, k] of CLUSTER.entries()) {
          const at = onCrystal(x, y, k);
          if (at) [hit, big] = [at, i === 2];
        }
        if (!hit) continue;
        const [d, t] = hit;
        let tone: PaletteKey = d < -0.4 ? 'w' : d < 0.1 ? '5' : d < 0.7 ? '4' : '3';
        if (big && d > 0.05 && d < 0.5 && t > 0.15 && t < 0.72) tone = 'c';
        if (big && Math.abs(t - (0.18 + frame * 0.17)) < 0.06 && d < 0.6) tone = 'w';
        c.set(x, y, tone);
      }
    // The cradle they grow from.
    for (let x = 24; x <= 44; x++) c.set(x, 13, x % 5 === 1 ? '2' : '4').set(x, 14, x % 5 === 1 ? '1' : '3');
    // A snowflake on its side.
    dots(c, 'w', [
      [35, 19],
      [35, 20],
      [35, 21],
      [34, 20],
      [36, 20],
    ]);
    dots(c, '5', [
      [33, 18],
      [37, 18],
      [33, 22],
      [37, 22],
      [35, 17],
      [35, 23],
      [32, 20],
      [38, 20],
    ]);
    // The trigger.
    box(c, 31, 27, 34, 29, [0, 1], (_x, y) => (y === 27 ? '4' : '2'), 'r');
    // The barrel: steel narrowing to the muzzle, ringed with frost.
    taper(c, 47, 57, 19.5, 4, 2.8, round(STEEL));
    for (const x of [49, 50, 54]) taper(c, x, x, 19.5, 4.3 - (x - 47) * 0.12, 4.3 - (x - 47) * 0.12, round(FROSTED));
    // An icicle at the muzzle, a cold light at its tip.
    taper(c, 58, 62, 19.5, 2.6, 1.2, (top, bottom) => (top === 0 ? 'w' : bottom === 0 ? '4' : '5'));
    dots(c, frame % 2 ? 'w' : 'c', [
      [62, 19],
      [62, 20],
    ]);
  });

export default sprite({
  name: 'pollinator_glacier',
  ...geometry,
  notes: `Permafrost (ADR-0160 amended, the Glacier set's gun): a crystal cryo cannon of steel frosted white along its top
edges, a cluster of three ice crystals growing out of a cradle on its back where the Pollinator's seed jar is (each lit
white on its left facet; the big one leaning forward with a cyan heart and a glint climbing it over 4 frames), ice
crusted over the front of the body, a snowflake on its side, a barrel ringed with frost narrowing to an icicle at the
muzzle (its tip blinking cold). Its flair: cold vapour sinking off it, cyan glints on the crystals. Rubric (4×): S4 R4
P4 L4 C4 A4 F4. Weakest: aimed right its white crystals meet the white fur collar behind them; the glint is a px wide.`,
  anims: { idle: { fps: 6, frames: [gun(0), gun(1), gun(2), gun(3)] } },
});
